/**
 * Shared scene utilities — anything a template wants to reuse goes here.
 * Materials, shape factories, GLB/Draco loader, HDRI loader.
 * Templates import these to build their own scene composition.
 */
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { RGBELoader } from 'three/addons/loaders/RGBELoader.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { materials } from '../look.js';

// Draco decoder served from Google CDN (cached across Three.js sites).
const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/');
dracoLoader.setDecoderConfig({ type: 'js' });

const gltfLoader = new GLTFLoader();
gltfLoader.setDRACOLoader(dracoLoader);

const PHYSICAL_MATERIALS = new Set(['glass', 'fabric', 'food', 'plastic', 'leather', 'porcelain']);

export function makeMaterial(product, { color, emissive = false } = {}) {
  const preset = materials[product.material] || materials.plastic;
  const usePhysical = PHYSICAL_MATERIALS.has(product.material);
  const Ctor = usePhysical ? THREE.MeshPhysicalMaterial : THREE.MeshStandardMaterial;
  const params = { color: new THREE.Color(color || product.colorA), ...preset };
  if (params.sheenColor) params.sheenColor = new THREE.Color(params.sheenColor);
  if (emissive) {
    params.emissive = new THREE.Color(product.accent);
    params.emissiveIntensity = 0.45;
  }
  return new Ctor(params);
}

export const shapes = {
  cylinder(p) {
    const g = new THREE.Group();
    const body = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.55, 0.22, 64), makeMaterial(p, { color: p.colorA }));
    body.rotation.x = Math.PI / 2; body.userData.role = 'body'; g.add(body);
    const face = new THREE.Mesh(new THREE.CylinderGeometry(0.48, 0.48, 0.04, 64), makeMaterial(p, { color: p.colorB }));
    face.rotation.x = Math.PI / 2; face.position.z = 0.12; face.userData.role = 'detail'; g.add(face);
    const tick = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.42, 0.01), makeMaterial(p, { color: p.accent, emissive: true }));
    tick.position.set(0, 0.18, 0.15); tick.userData.role = 'accent';
    g.add(tick); g.userData.spinner = tick;
    return g;
  },
  sphere(p) {
    const g = new THREE.Group();
    const ball = new THREE.Mesh(new THREE.SphereGeometry(0.7, 64, 64), makeMaterial(p, { color: p.colorA }));
    ball.userData.role = 'body'; g.add(ball);
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.78, 0.04, 16, 80), makeMaterial(p, { color: p.colorB }));
    ring.userData.role = 'detail'; g.add(ring); g.userData.spinner = ring;
    const dot = new THREE.Mesh(new THREE.SphereGeometry(0.08, 24, 24), makeMaterial(p, { color: p.accent, emissive: true }));
    dot.position.set(0.45, 0.45, 0.35); dot.userData.role = 'accent'; g.add(dot);
    return g;
  },
  cube(p) {
    const g = new THREE.Group();
    const body = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), makeMaterial(p, { color: p.colorA }));
    body.userData.role = 'body'; g.add(body);
    const inner = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.5, 0.5), makeMaterial(p, { color: p.colorB }));
    inner.position.set(0.25, 0.25, 0.5); inner.userData.role = 'detail';
    g.add(inner); g.userData.spinner = inner;
    const edge = new THREE.Mesh(new THREE.BoxGeometry(1.02, 0.04, 0.04), makeMaterial(p, { color: p.accent, emissive: true }));
    edge.position.set(0, 0.5, 0.5); edge.userData.role = 'accent'; g.add(edge);
    return g;
  },
  torus(p) {
    const g = new THREE.Group();
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.6, 0.22, 32, 120), makeMaterial(p, { color: p.colorA }));
    ring.userData.role = 'body'; g.add(ring); g.userData.spinner = ring;
    const core = new THREE.Mesh(new THREE.SphereGeometry(0.18, 32, 32), makeMaterial(p, { color: p.colorB }));
    core.userData.role = 'detail'; g.add(core);
    const spark = new THREE.Mesh(new THREE.SphereGeometry(0.06, 16, 16), makeMaterial(p, { color: p.accent, emissive: true }));
    spark.position.set(0.6, 0.3, 0.2); spark.userData.role = 'accent'; g.add(spark);
    return g;
  },
  capsule(p) {
    const g = new THREE.Group();
    const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.35, 0.7, 16, 32), makeMaterial(p, { color: p.colorA }));
    body.userData.role = 'body'; g.add(body); g.userData.spinner = body;
    const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.36, 0.36, 0.18, 32), makeMaterial(p, { color: p.colorB }));
    cap.position.y = 0.55; cap.userData.role = 'detail'; g.add(cap);
    const dot = new THREE.Mesh(new THREE.SphereGeometry(0.05, 16, 16), makeMaterial(p, { color: p.accent, emissive: true }));
    dot.position.set(0, 0.6, 0.38); dot.userData.role = 'accent'; g.add(dot);
    return g;
  },
  bottle(p) {
    const g = new THREE.Group();
    const body = new THREE.Mesh(new THREE.CylinderGeometry(0.4, 0.4, 0.9, 48), makeMaterial(p, { color: p.colorA }));
    body.position.y = -0.1; body.userData.role = 'body'; g.add(body);
    const shoulder = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.4, 0.2, 48), makeMaterial(p, { color: p.colorA }));
    shoulder.position.y = 0.45; shoulder.userData.role = 'body'; g.add(shoulder);
    const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.16, 0.18, 32), makeMaterial(p, { color: p.colorB }));
    neck.position.y = 0.65; neck.userData.role = 'detail';
    g.add(neck); g.userData.spinner = neck;
    const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.2, 0.18, 32), makeMaterial(p, { color: p.accent, emissive: true }));
    cap.position.y = 0.82; cap.userData.role = 'accent'; g.add(cap);
    return g;
  },
  // ── Beauty: wide squat jar + lid (creams, balms, masks) ──
  jar(p) {
    const g = new THREE.Group();
    const body = new THREE.Mesh(new THREE.CylinderGeometry(0.6, 0.58, 0.5, 64), makeMaterial(p, { color: p.colorA }));
    body.position.y = -0.12; body.userData.role = 'body'; g.add(body);
    const lid = new THREE.Mesh(new THREE.CylinderGeometry(0.62, 0.62, 0.28, 64), makeMaterial(p, { color: p.colorB }));
    lid.position.y = 0.28; lid.userData.role = 'detail'; g.add(lid); g.userData.spinner = lid;
    const knob = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.18, 0.04, 48), makeMaterial(p, { color: p.accent, emissive: true }));
    knob.position.y = 0.44; knob.userData.role = 'accent'; g.add(knob);
    return g;
  },
  // ── Beauty: slim cosmetic tube / dropper (serum, lipstick, foundation) ──
  tube(p) {
    const g = new THREE.Group();
    const body = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.26, 1.0, 48), makeMaterial(p, { color: p.colorA }));
    body.position.y = -0.05; body.userData.role = 'body'; g.add(body);
    const shoulder = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.22, 0.16, 48), makeMaterial(p, { color: p.colorA }));
    shoulder.position.y = 0.53; shoulder.userData.role = 'body'; g.add(shoulder);
    const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 0.34, 48), makeMaterial(p, { color: p.colorB }));
    cap.position.y = 0.75; cap.userData.role = 'detail'; g.add(cap); g.userData.spinner = cap;
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.13, 0.02, 16, 48), makeMaterial(p, { color: p.accent, emissive: true }));
    ring.rotation.x = Math.PI / 2; ring.position.y = 0.58; ring.userData.role = 'accent'; g.add(ring);
    return g;
  },
  // ── Supplements: stand-up pouch / sachet (protein, greens) ──
  pouch(p) {
    const g = new THREE.Group();
    const body = new THREE.Mesh(new THREE.BoxGeometry(0.85, 1.1, 0.32), makeMaterial(p, { color: p.colorA }));
    body.userData.role = 'body';
    // round the corners with a bevel modifier for a soft-pack read
    g.add(body); g.userData.spinner = body;
    const seal = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.14, 0.06), makeMaterial(p, { color: p.colorB }));
    seal.position.y = 0.6; seal.userData.role = 'detail'; g.add(seal);
    const strip = new THREE.Mesh(new THREE.BoxGeometry(0.86, 0.12, 0.34), makeMaterial(p, { color: p.accent, emissive: true }));
    strip.position.y = -0.2; strip.userData.role = 'accent'; g.add(strip);
    return g;
  },
  // ── Apparel: folded garment panel (tee, hoodie). v1 = stylized soft panel.
  //    Production path: swap to a real Blender cloth-sim .glb via product.model.
  garment(p) {
    const g = new THREE.Group();
    // Main folded body: a flat, wide, beveled slab.
    const body = new THREE.Mesh(new THREE.BoxGeometry(1.1, 1.2, 0.22), makeMaterial(p, { color: p.colorA }));
    body.userData.role = 'body'; g.add(body); g.userData.spinner = body;
    // Folded sleeve hint: two angled flaps.
    const sleeveL = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.5, 0.2), makeMaterial(p, { color: p.colorA }));
    sleeveL.position.set(-0.5, 0.32, 0.02); sleeveL.rotation.z = 0.5; sleeveL.userData.role = 'body'; g.add(sleeveL);
    const sleeveR = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.5, 0.2), makeMaterial(p, { color: p.colorA }));
    sleeveR.position.set(0.5, 0.32, 0.02); sleeveR.rotation.z = -0.5; sleeveR.userData.role = 'body'; g.add(sleeveR);
    // Collar / neckline detail.
    const collar = new THREE.Mesh(new THREE.TorusGeometry(0.2, 0.05, 16, 48, Math.PI), makeMaterial(p, { color: p.colorB }));
    collar.position.set(0, 0.5, 0.12); collar.userData.role = 'detail'; g.add(collar);
    // Brand tab accent.
    const tab = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.16, 0.24), makeMaterial(p, { color: p.accent, emissive: true }));
    tab.position.set(0.35, -0.45, 0.02); tab.userData.role = 'accent'; g.add(tab);
    return g;
  },
};

export function buildPrimitive(product) {
  const factory = shapes[product.shape] || shapes.cylinder;
  return factory(product);
}

export function loadModel(product, onComplete) {
  const group = new THREE.Group();
  gltfLoader.load(
    product.model,
    (gltf) => {
      const root = gltf.scene;
      const box = new THREE.Box3().setFromObject(root);
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      const scale = (product.modelScale || 1.4) / (maxDim || 1);
      root.scale.setScalar(scale);
      box.setFromObject(root);
      const center = box.getCenter(new THREE.Vector3());
      root.position.sub(center);

      if (!product.keepMaterials) {
        const preset = materials[product.material] || materials.plastic;
        root.traverse((node) => {
          if (node.isMesh && node.material) {
            const mat = node.material.clone();
            mat.color = new THREE.Color(product.colorA);
            for (const [k, v] of Object.entries(preset)) {
              if (k in mat) mat[k] = (k === 'sheenColor') ? new THREE.Color(v) : v;
            }
            if ('emissive' in mat) mat.emissive = new THREE.Color(product.accent).multiplyScalar(0.15);
            node.material = mat;
          }
        });
      }
      group.add(root);
      group.userData.spinner = root;
      onComplete?.(group);
    },
    undefined,
    (err) => {
      console.warn(`[scene-utils] model load failed for ${product.id}, falling back`, err);
      group.add(buildPrimitive(product));
      onComplete?.(group);
    },
  );
  return group;
}

export function buildMesh(product) {
  if (product.model) return loadModel(product);
  return buildPrimitive(product);
}

/**
 * Set up PMREM environment with optional HDRI hot-swap. Returns the disposer.
 */
export function setupEnvironment(renderer, scene, { hdriUrl = null, hdriAsBackground = false, envIntensity = 0.9 } = {}) {
  const pmrem = new THREE.PMREMGenerator(renderer);
  pmrem.compileEquirectangularShader();
  const roomEnv = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  scene.environment = roomEnv;
  scene.environmentIntensity = envIntensity;

  let hdriTexture = null;
  if (hdriUrl) {
    new RGBELoader().load(
      hdriUrl,
      (texture) => {
        texture.mapping = THREE.EquirectangularReflectionMapping;
        const envMap = pmrem.fromEquirectangular(texture).texture;
        scene.environment = envMap;
        if (hdriAsBackground) scene.background = envMap;
        roomEnv.dispose();
        hdriTexture = texture;
      },
      undefined,
      (err) => console.warn('[scene-utils] HDRI load failed', err),
    );
  }

  return () => {
    pmrem.dispose();
    roomEnv?.dispose?.();
    hdriTexture?.dispose?.();
  };
}

/**
 * Generic role-based retint: walks a product mesh group, mutates color per role.
 */
export function retintMesh(meshGroup, { colorA, colorB, accent } = {}) {
  meshGroup.traverse((node) => {
    if (!node.isMesh || !node.material) return;
    const role = node.userData.role;
    if (role === 'body' && colorA) node.material.color.set(colorA);
    else if (role === 'detail' && colorB) node.material.color.set(colorB);
    else if (role === 'accent' && accent) {
      node.material.color.set(accent);
      if (node.material.emissive) node.material.emissive.set(accent);
    }
  });
}

/**
 * Tonemap map — shared by all templates.
 */
export const TONEMAPS = {
  aces: THREE.ACESFilmicToneMapping,
  reinhard: THREE.ReinhardToneMapping,
  cineon: THREE.CineonToneMapping,
  linear: THREE.LinearToneMapping,
  none: THREE.NoToneMapping,
};
